/**
 * Ad slot component that automatically fetches and displays appropriate ads
 * Handles different ad types and placements
 */

import { memo, useMemo } from "react";
import { useAds, Ad } from "@/hooks/useAds";
import AdBanner from "./AdBanner";
import AdSidebar from "./AdSidebar";

interface AdSlotProps {
  placement: 'home' | 'listings' | 'detail' | 'search' | 'member_dashboard' | 'member_profile' | 'patient_dashboard' | 'homecare' | 'ambulance' | 'bloodbank' | 'healthcamp' | 'all';
  type?: 'banner' | 'sidebar' | 'inline' | 'compact' | 'featured';
  categoryType?: number;
  maxAds?: number;
  className?: string;
}

const AdSlot = memo(({ 
  placement, 
  type = 'banner', 
  categoryType, 
  maxAds = 1,
  className = "" 
}: AdSlotProps) => {
  const { data: ads, isLoading } = useAds(placement, categoryType);

  // Filter and limit ads by type
  const displayAds = useMemo(() => {
    if (!ads) return [];
    
    const filteredAds = ads.filter(ad => {
      if (type === 'banner') return ad.ad_type === 'banner';
      if (type === 'featured') return ad.ad_type === 'featured';
      if (type === 'inline') return ad.ad_type === 'inline' || ad.ad_type === 'sidebar' || ad.ad_type === 'featured';
      if (type === 'sidebar' || type === 'compact') {
        return ad.ad_type === 'sidebar' || ad.ad_type === 'featured';
      }
      return true;
    });

    return filteredAds.slice(0, maxAds);
  }, [ads, type, maxAds]);

  if (isLoading) {
    // Skeleton loader — aspect-ratio matches the real ad's image area
    // (AdBanner.tsx is aspect-[3/1], AdSidebar.tsx card variant is
    // aspect-[8/7]) so there's no height jump/layout shift once the real
    // ad swaps in. Previously used unrelated fixed h-* breakpoints that
    // didn't match either real component's height at any viewport.
    return (
      <div className={`${className}`}>
        {type === 'banner' ? (
          <div className="w-full aspect-[3/1] rounded-xl xs:rounded-2xl bg-gradient-to-r from-muted via-muted/80 to-muted animate-pulse" />
        ) : (
          <div className="w-full aspect-[8/7] rounded-xl xs:rounded-2xl bg-muted animate-pulse" />
        )}
      </div>
    );
  }

  if (!displayAds.length) return null;

  // For sidebar type with multiple ads, render items directly (no wrapper) for parent grid
  if (type === 'sidebar' && displayAds.length > 1) {
    return (
      <>
        {displayAds.map((ad) => (
          <AdSidebar 
            key={ad.id} 
            ad={ad} 
            variant="card"
            className={className}
          />
        ))}
      </>
    );
  }

  return (
    <div className={`${className}`}>
      {type === 'banner' ? (
        <div className="space-y-4">
          {displayAds.map((ad) => (
            <AdBanner key={ad.id} ad={ad} />
          ))}
        </div>
      ) : (
        <div className={type === 'inline' ? 'space-y-3' : type === 'compact' ? 'space-y-2' : ''}>
          {displayAds.map((ad) => (
            <AdSidebar 
              key={ad.id} 
              ad={ad} 
              variant={type === 'sidebar' || type === 'featured' ? 'card' : type as 'card' | 'inline' | 'compact'}
            />
          ))}
        </div>
      )}
    </div>
  );
});

AdSlot.displayName = 'AdSlot';

export default AdSlot;
